import { cn, formatIntNumber, getOffset } from '@anytag/shared-utils';
import { useParamsSearch } from '@anytag/shared-hooks';
import { INITIAL_PAGE_NUMBER, LIMIT } from '@anytag/shared-constants';
import { Icon } from '../Icon';

export interface PaginationProps {
  className?: string;
  totalRecords?: number;
}

export const Pagination = ({ className, totalRecords = 0 }: PaginationProps) => {
  const { page = INITIAL_PAGE_NUMBER, limit = LIMIT, setPage } = useParamsSearch();

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const totalPages = Math.ceil(totalRecords / (limit || 1)); // avoid limit = 0
  const firstIndex = getOffset(page, limit) + 1;
  const lastIndex = Math.min(firstIndex + limit - 1, totalRecords);
  const pageNumbers = (() => {
    const length = Math.min(totalPages, 5);
    const slide = Math.floor(length / 2);

    let startIndex: number;
    startIndex = Math.max(1, page - slide);
    startIndex = Math.min(startIndex, totalPages - length + 1);

    const allPageNumbers = Array.from(Array(length), (_, n) => n + startIndex);

    return allPageNumbers.filter((n) => n < totalPages);
  })();

  if (!totalRecords || totalPages <= 1) {
    return null;
  }

  return (
    <div className={cn('mt-2.5 flex h-11 items-center justify-between', className)}>
      <div className="text-default text-tertiary">
        {formatIntNumber(firstIndex)}-{formatIntNumber(lastIndex)} / {formatIntNumber(totalRecords)}
      </div>

      <div className="flex items-center">
        <button
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
          className={cn('mr-3', styles.arrow(page <= 1))}
        >
          <Icon size={12} variant="gray" icon="caret-left" className="mb-1" />
        </button>

        {/* nav to first page */}
        {(pageNumbers[0] as number) > 2 ? (
          <>
            <button className={styles.page()} onClick={() => handlePageChange(1)}>
              1
            </button>
            <div className="w-6">...</div>
          </>
        ) : null}

        {pageNumbers.map((pageNumber) => (
          <button
            key={pageNumber}
            disabled={pageNumber === page}
            className={styles.page(pageNumber === page)}
            onClick={() => handlePageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}

        {/* nav to last page */}
        {totalPages > 1 ? (
          <>
            {(pageNumbers[pageNumbers.length - 1] as number) < totalPages - 1 ? <div className="w-6">...</div> : null}
            <button
              disabled={totalPages === page}
              className={styles.page(totalPages === page)}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        ) : null}

        <button
          disabled={page >= totalPages}
          className={styles.arrow(page >= totalPages)}
          onClick={() => handlePageChange(page + 1)}
        >
          <Icon size={12} variant="gray" icon="caret-right" className="mb-1" />
        </button>
      </div>
    </div>
  );
};
const styles = {
  arrow: (disabled?: boolean) =>
    cn(
      'box-border h-7 w-20 rounded-sm border border-black-30 p-0',
      disabled
        ? 'border-black-30 bg-black-30! opacity-50'
        : 'cursor-pointer bg-white hover:border-black-40 hover:text-black-90'
    ),
  page: (disabled?: boolean) =>
    cn(
      'mr-3 size-7 min-w-7 rounded-sm text-sm leading-7 font-normal text-black-90',
      disabled
        ? 'bg-black-90 text-white'
        : 'cursor-pointer border border-black-30 bg-white hover:border-black-40 hover:text-black-90'
    )
};
